import { createQueryString, formatPhoneNumber } from '@toss/utils';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import JsBarcode from 'jsbarcode';
import type { CellValue, JspreadsheetInstance } from 'jspreadsheet-ce';
import { compact } from 'lodash-es';

import type { Shops } from '@/Swagger';
import axiosInstance from '@/axios/axios';

import type { 코너로지스_주문_테이블_데이터_타입 } from './types';

/**
 * 코너로지스 API 관련 헬퍼 함수들.
 */
export namespace CornerLogis {
  /**
   * 코너로지스 API 프록시 요청 타입.
   */
  type ProxyRequestType = Omit<Shops.CornerLogisProxyCreate.RequestBody, 'body' | 'method'> & {
    shopID: string;
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: object;
    params?: object;
  };

  /**
   * 코너로지스 API 프록시 함수.
   */
  export const proxy = <T>(request: ProxyRequestType, config?: AxiosRequestConfig) => {
    const { shopID, body, path, params, ...rest } = request;
    const query = params ? createQueryString(params) : '';

    return axiosInstance
      .post<T>(
        `/shops/${shopID}/corner-logis/proxy`,
        {
          ...rest,
          ...(rest.method !== 'GET' && body != null ? { header: { 'Content-Type': 'application/json' } } : {}),
          body: JSON.stringify(body),
          path: `${path}${query}`,
        } satisfies Shops.CornerLogisProxyCreate.RequestBody,
        config,
      )
      .then(v => v.data);
  };

  /**
   * 코너로지스 API 에러 메시지 가져오는 함수.
   */
  export const getErrorMessage = (error: AxiosError<any, any>, fallback?: string) => {
    return error.response?.data?.error.message ?? fallback ?? '알 수 없는 오류가 발생했습니다. 다시 시도해주세요.';
  };

  /**
   * 코너로지스 API 에러 코드 가져오는 함수.
   */
  export const getErrorCode = (error: AxiosError<any, any>) => {
    return error.response?.data?.error.code;
  };

  /**
   * 전화번호 포맷 변환 함수.
   * @param phoneNumber 변환할 전화번호 (하이픈 포함, 미포함 모두 가능)
   */
  export const convertFormatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) {
      return;
    }

    return formatPhoneNumber(phoneNumber.replace(/\D/g, ''));
  };

  /*
   * 바코드 인쇄 데이터 타입.
   */
  type BarcodeDataType = {
    /**
     * 바코드 데이터.
     * @description 바코드에 인쇄되는 데이터입니다.
     */
    barcodeData: string;
    /**
     * 추가 데이터.
     * @description 바코드 데이터와 함께 인쇄되는 추가 정보입니다.
     */
    extraData?: string;
  };

  /**
   * 함수 호출 시, 바코드 일괄 인쇄 페이지가 팝업됩니다.
   *
   * @param data BarcodeDataType[] 바코드 데이터 배열
   */
  export const 바코드_인쇄하기 = (data: BarcodeDataType[]) => {
    const imagesHtml = data
      .map(({ barcodeData, extraData }) => {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, barcodeData, {
          margin: 100,
          font: 'arial',
          width: 6,
          height: 200,
          fontSize: 56,
          text: `${barcodeData} ${extraData}`,
        });
        const imageURL = canvas.toDataURL('image/png', 1.0);
        return `<img src="${imageURL}" style="margin-bottom: 20px;" />`;
      })
      .join('');

    const printWindow = window.open('', '_blank');

    if (printWindow === null) {
      return;
    }

    printWindow.document.write(`
    <html>
      <head>
        <title>패키지 바코드</title>
        <style>
          body { display: flex; flex-direction: column; justify-content: center; align-items: center; }
        </style>
      </head>
      <body>
          ${imagesHtml}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const 병합_셀_리스트: Array<keyof 코너로지스_주문_테이블_데이터_타입[number]> = [
    'check',
    '주문출고번호',
    '쇼핑몰주문번호',
    '쇼핑몰',
    '우편번호',
    '배송지주소',
    '고객연락처',
    '배송메시지',
    '반품운송장번호',
    '반품우편번호',
    '반품주소',
    '반품고객연락처',
    '반품메시지',
  ];

  type 병합_셀_이름_타입 = (typeof 병합_셀_리스트)[number];

  /**
   * 코너로지스 주문 테이블을 머지합니다.
   */
  export function mergeCell(
    orderList: 코너로지스_주문_테이블_데이터_타입,
    instance: jspreadsheet.JspreadsheetInstance,
    mergeCheckCell: boolean,
  ) {
    merge(0, orderList.length - 1, orderList, '주문출고번호', instance, mergeCheckCell);
  }

  function merge(
    beginRowIndex: number,
    endRowIndex: number,
    orderList: 코너로지스_주문_테이블_데이터_타입,
    baseCellName: 병합_셀_이름_타입,
    instance: jspreadsheet.JspreadsheetInstance,
    mergeCheckCell: boolean,
  ) {
    let startRowIndex = 0;
    let prevCellData: undefined | string | number | boolean;

    let allMerge = true;

    for (let rowIndex = beginRowIndex; rowIndex <= endRowIndex; ++rowIndex) {
      if (baseCellName === 'check') {
        return;
      }
      const baseCellData = orderList[rowIndex]?.[baseCellName];

      // 시작 row 확인 & 스킵
      if (prevCellData == null) {
        startRowIndex = rowIndex;
        prevCellData = baseCellData;
        continue;
      }

      if (prevCellData !== baseCellData) {
        if (rowIndex !== startRowIndex + 1) {
          allMerge = false;
          const 주문출고번호_인덱스 = instance.options.columns?.findIndex(v => v.name === baseCellName) ?? 0;

          for (let i = startRowIndex + 1; i < rowIndex; i++) {
            const cell = instance.getCell([주문출고번호_인덱스, i]);
            if (cell == null) {
              continue;
            }
            cell.classList.add('merge_cell');
          }
          if (baseCellName === '주문출고번호') {
            if (mergeCheckCell) {
              const checkBoxIDIndex = instance.options.columns?.findIndex(v => v.name === 'check') ?? 0;
              for (let i = startRowIndex + 1; i < rowIndex; i++) {
                const cell = instance.getCell([checkBoxIDIndex, i]);
                if (cell == null) {
                  continue;
                }
                cell.classList.add('merge_cell');
              }
            }
            병합_셀_리스트
              .filter(v => v !== '주문출고번호' && v !== 'check')
              .forEach(title => {
                merge(startRowIndex, rowIndex - 1, orderList, title, instance, mergeCheckCell);
              });
          }
        }
        startRowIndex = rowIndex;
        prevCellData = baseCellData;
        continue;
      }

      if (rowIndex === endRowIndex && baseCellData === prevCellData && !allMerge) {
        const 주문출고번호_인덱스 = instance.options.columns?.findIndex(v => v.name === baseCellName) ?? 0;

        for (let i = startRowIndex + 1; i <= rowIndex; i++) {
          const cell = instance.getCell([주문출고번호_인덱스, i]);
          if (cell == null) {
            continue;
          }
          cell.classList.add('merge_cell');
        }
        if (baseCellName === '주문출고번호') {
          if (mergeCheckCell) {
            const checkBoxIDIndex = instance.options.columns?.findIndex(v => v.name === 'check') ?? 0;
            for (let i = startRowIndex + 1; i <= rowIndex; i++) {
              const cell = instance.getCell([checkBoxIDIndex, i]);
              if (cell == null) {
                continue;
              }
              cell.classList.add('merge_cell');
            }
          }
          병합_셀_리스트
            .filter(v => v !== '주문출고번호' && v !== 'check')
            .forEach(title => {
              merge(startRowIndex, rowIndex, orderList, title, instance, mergeCheckCell);
            });
        }
      }
      if (prevCellData === baseCellData) {
        continue;
      }
    }

    if (allMerge) {
      const colIndex = instance.options.columns?.findIndex(v => v.name === baseCellName) ?? 0;
      for (let i = startRowIndex + 1; i <= endRowIndex; i++) {
        const cell = instance.getCell([colIndex, i]);
        if (cell == null) {
          continue;
        }
        cell.classList.add('merge_cell');
      }

      if (baseCellName === '주문출고번호') {
        if (mergeCheckCell) {
          const checkBoxIDIndex = instance.options.columns?.findIndex(v => v.name === 'check') ?? 0;
          for (let i = startRowIndex + 1; i <= endRowIndex; i++) {
            const cell = instance.getCell([checkBoxIDIndex, i]);
            if (cell == null) {
              continue;
            }
            cell.classList.add('merge_cell');
          }
        }
        병합_셀_리스트
          .filter(v => v !== '주문출고번호' && v !== 'check')
          .forEach(title => {
            merge(startRowIndex, endRowIndex, orderList, title, instance, mergeCheckCell);
          });
      }
    }
  }

  /**
   * 선택된 셀의 데이터를 가져옵니다.
   * @param sheetRef Jspreadsheet 인스턴스
   * @param columnName 가져올 컬럼 이름
   */
  export function getSelectedCellData({
    sheetRef,
    columnName,
  }: {
    sheetRef: React.MutableRefObject<JspreadsheetInstance | undefined>;
    columnName: keyof 코너로지스_주문_테이블_데이터_타입[number];
  }): string[] {
    const checkColumn = sheetRef.current?.options.columns?.findIndex(v => v.name === 'check') ?? -1;
    const columnIndex = sheetRef.current?.options.columns?.findIndex(v => v.name === columnName) ?? -1;

    if (checkColumn !== -1) {
      return compact(
        (sheetRef.current?.getData() as CellValue[][] | undefined)?.map(rowData => {
          const checkValue = rowData[checkColumn];

          if (checkValue === true) {
            return rowData[columnIndex] as string;
          } else {
            return undefined;
          }
        }),
      );
    } else {
      return [];
    }
  }
}
